import { FC, useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { Clear, Search } from '@mui/icons-material';
import styles from './TopSearchBarInputField.module.scss';

interface TopSearchBarInputFieldProps {
    searchTerm?: string;
    onSearchTermChange?: (term: string) => void;
    onSearch?: (term: string) => void;
    onBlur?: () => void;
    onArrowKey?: (direction: 'up' | 'down') => void;
    /** Callback function to run when user presses the enter key inside the search bar. */
    onEnterKey?: () => void;
}

export const TopSearchBarInputField: FC<TopSearchBarInputFieldProps> = ({
    searchTerm,
    onSearchTermChange,
    onSearch,
    onBlur,
    onArrowKey,
    onEnterKey,
}) => {
    const [searchDebounceId, setSearchDebounceId] =
        useState<NodeJS.Timeout | null>(null);

    const searchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!!onSearchTermChange) onSearchTermChange(event.target.value);
        if (searchDebounceId !== null) clearTimeout(searchDebounceId);
        setSearchDebounceId(
            setTimeout(() => {
                if (!!onSearch) onSearch(event.target.value);
            }, 500)
        );
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!onArrowKey) return;
        if (event.key === 'ArrowUp') onArrowKey('up');
        else if (event.key === 'ArrowDown') onArrowKey('down');
        else if (onEnterKey && event.key === 'Enter') onEnterKey();
        if (
            event.key === 'ArrowUp' ||
            event.key === 'ArrowDown' ||
            event.key === 'Enter'
        )
            event.preventDefault();
    };
    const clearSearch = () => {
        if (!!onSearchTermChange) onSearchTermChange('');
        if (!!onSearch) onSearch('');
    };

    return (
        <TextField
            data-testid="TopSearchBarInputField"
            autoFocus
            value={searchTerm}
            onChange={searchChanged}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={styles.TopSearchBarInputField}
            variant="standard"
            placeholder="Search Monitors..."
            sx={{
                width: '100%',
                '>*': {
                    backgroundColor: '#4c4c4c',
                    width: '100%',
                },
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search
                            fontSize="small"
                            sx={{
                                color: '#fafafa',
                                paddingLeft: '5px',
                            }}
                        />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        {searchTerm !== '' ? (
                            <IconButton onMouseDown={clearSearch}>
                                <Clear
                                    fontSize="medium"
                                    sx={{
                                        color: '#fafafa',
                                    }}
                                />
                            </IconButton>
                        ) : (
                            <div className={styles.clearButtonPlaceholder} />
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
};
